import React, { useEffect, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useAdmin } from '../../context/AdminContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faImage, faTag, faUtensils, faCoins, faMapMarkerAlt, faCamera } from '@fortawesome/free-solid-svg-icons';
import { useVenueId } from '../../hooks/useUserData';
import { uploadImageWithCleanup } from '../../utils/imageService';
import UploadImageModal from './UploadImageModal';
import LoadingSpinner from '../LoadingSpinner';
import AdminFormModal from '../shared/AdminFormModal';
import AdminNotice from '../shared/AdminNotice';
import ModifierGroupsManager from './ModifierGroupsManager';

const EditItemModal = ({ show, onClose, item, categories, uncategorizedCategory, onItemUpdated }) => {
  const venueId = useVenueId();
  const { areas } = useAdmin();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [areaId, setAreaId] = useState('');
  const [kdvRate, setKdvRate] = useState('');
  const [isAvailable, setIsAvailable] = useState(true);
  const [modifierGroups, setModifierGroups] = useState([]);
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const initialItem = useRef(item);

  const isUncategorized = uncategorizedCategory && item && item.categoryId === uncategorizedCategory.id;
  const selectableCategories = categories.filter((category) => !category.isSystemCategory);

  useEffect(() => {
    if (item) {
      setName(item.name);
      setDescription(item.description);
      setPrice(item.price);
      setImageUrl(item.imageUrl);
      setCategoryId(isUncategorized ? '' : item.categoryId);
      setAreaId(item.areaId || '');
      setKdvRate(item.kdvRate || '10');
      setIsAvailable(item.isAvailable ?? true);
      setModifierGroups(item.modifierGroups || []);
      initialItem.current = item;
    }
  }, [item, isUncategorized]);

  const handleImageUpload = async () => {
    if (!imageFile) {
      return;
    }

    setLoading(true);

    try {
      const newImageUrl = await uploadImageWithCleanup(imageFile, venueId, initialItem.current.imageUrl, undefined, item.id);
      if (!newImageUrl) {
        return;
      }

      setImageUrl(newImageUrl);
      await onItemUpdated({ id: item.id, imageUrl: newImageUrl });
      initialItem.current = { ...initialItem.current, imageUrl: newImageUrl };
    } finally {
      setLoading(false);
      setIsUploadModalVisible(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    const updatedFields = {};
    if (name !== initialItem.current.name) updatedFields.name = name;
    if (description !== initialItem.current.description) updatedFields.description = description;
    if (parseFloat(price) !== initialItem.current.price) updatedFields.price = parseFloat(price);
    if (imageUrl !== initialItem.current.imageUrl) updatedFields.imageUrl = imageUrl;
    if (parseInt(categoryId, 10) !== initialItem.current.categoryId) updatedFields.categoryId = parseInt(categoryId, 10);
    const newAreaId = areaId ? parseInt(areaId, 10) : null;
    if (newAreaId !== initialItem.current.areaId) updatedFields.areaId = newAreaId;
    if (parseFloat(kdvRate) !== initialItem.current.kdvRate) updatedFields.kdvRate = parseFloat(kdvRate);
    if (isAvailable !== (initialItem.current.isAvailable ?? true)) updatedFields.isAvailable = isAvailable;
    const currentModifierGroupIds = JSON.stringify((initialItem.current.modifierGroups || []).map((group) => group.id));
    const nextModifierGroupIds = JSON.stringify((modifierGroups || []).map((group) => group.id));
    if (currentModifierGroupIds !== nextModifierGroupIds) {
      updatedFields.modifierGroups = (modifierGroups || []).map((group, index) => ({
        id: group.id,
        sortOrder: index + 1,
      }));
    }

    const selectedCategory = categories.find((category) => category.id === parseInt(categoryId, 10));
    if (selectedCategory && selectedCategory.menuId !== initialItem.current.menuId) {
      updatedFields.menuId = selectedCategory.menuId;
    }

    if (Object.keys(updatedFields).length > 0) {
      await onItemUpdated({ id: item.id, ...updatedFields });
    }

    setLoading(false);
    onClose();
  };

  const handlePersistModifierGroups = async (nextGroups) => {
    const updated = await onItemUpdated(
      {
        id: item.id,
        modifierGroups: (nextGroups || []).map((group, index) => ({
          id: group.id,
          sortOrder: index + 1,
        })),
      },
      {
        keepModalOpen: true,
        suppressNotice: true,
      }
    );

    const persistedGroups = updated?.modifierGroups || nextGroups || [];
    setModifierGroups(persistedGroups);
    initialItem.current = {
      ...initialItem.current,
      modifierGroups: persistedGroups,
    };
    return persistedGroups;
  };

  return (
    <>
      {loading ? <LoadingSpinner /> : null}
      <AdminFormModal
        show={show}
        onClose={onClose}
        title={isUncategorized ? 'Kategori Ata ve Ürünü Düzenle' : 'Ürünü Düzenle'}
        subtitle="Temel bilgi, görsel, kategori ve alan ayarlarını aynı form içinde yönetin."
        icon={faEdit}
        dialogClassName="admin-modal admin-modal-wide admin-modal-item-edit"
        notice={
          isUncategorized ? (
            <AdminNotice
              tone="warning"
              title="Kategori bekleniyor"
              message="Bu ürün müşteriye gösterilmeden önce bir kategoriye taşınmalıdır."
            />
          ) : null
        }
        footer={(
          <>
            <div className="admin-modal-footer-secondary">
              <Button variant="secondary" onClick={onClose} className="modal-cancel-btn">
                İptal
              </Button>
            </div>
            <div className="admin-modal-footer-primary">
              <Button variant="primary" onClick={handleSubmit} className="modal-save-btn modal-save-btn-success" disabled={loading}>
                {loading ? 'Kaydediliyor...' : 'Kaydet'}
              </Button>
            </div>
          </>
        )}
      >
        <Form onSubmit={handleSubmit} className="admin-form-section edit-item-form">
          <div className="admin-form-block edit-item-image-block">
            <div className="admin-form-block-title">
              <FontAwesomeIcon icon={faImage} />
              Görsel
            </div>
            <div className="edit-item-image-layout">
              {imageUrl ? (
                <img src={imageUrl} alt={name || item?.name} className="admin-detail-image edit-item-image-preview" />
              ) : (
                <div className="admin-detail-placeholder edit-item-image-placeholder">
                  <FontAwesomeIcon icon={faImage} size="2x" />
                  <span>Bu ürün için görsel yüklenmedi.</span>
                </div>
              )}
              <Button variant="secondary" onClick={() => setIsUploadModalVisible(true)} className="modal-neutral-btn edit-item-image-button">
                <FontAwesomeIcon icon={faCamera} />
                Görseli Düzenle
              </Button>
            </div>
          </div>

          <div className="admin-form-block edit-item-basic-block">
            <div className="admin-form-block-title">
              <FontAwesomeIcon icon={faUtensils} />
              Temel Bilgiler
            </div>
            <div className="admin-form-grid">
              <div className="admin-modal-field">
                <Form.Label>Ürün Adı</Form.Label>
                <Form.Control type="text" value={name} onChange={(event) => setName(event.target.value)} required className="form-input" />
              </div>
              <div className="admin-modal-field">
                <Form.Label>
                  <FontAwesomeIcon icon={faCoins} className="form-icon" />
                  Fiyat
                </Form.Label>
                <Form.Control type="number" step="0.01" value={price} onChange={(event) => setPrice(event.target.value)} required className="form-input" />
              </div>
              <div className="admin-modal-field" style={{ gridColumn: '1 / -1' }}>
                <Form.Label>Açıklama</Form.Label>
                <Form.Control as="textarea" rows={3} value={description} onChange={(event) => setDescription(event.target.value)} className="form-input" />
              </div>
            </div>
          </div>

          <div className="admin-form-block edit-item-scope-block">
            <div className="admin-form-block-title">
              <FontAwesomeIcon icon={faTag} />
              Kapsam
            </div>
            <div className="admin-form-grid">
              <div className="admin-modal-field">
                <Form.Label>Kategori</Form.Label>
                <Form.Select value={categoryId} onChange={(event) => setCategoryId(event.target.value)} required className="form-input">
                  {isUncategorized ? <option value="">-- Kategori Seçin --</option> : null}
                  {selectableCategories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div className="admin-modal-field">
                <Form.Label>
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="form-icon" />
                  Alan
                </Form.Label>
                <Form.Select value={areaId} onChange={(event) => setAreaId(event.target.value)} className="form-input">
                  <option value="">Tümü</option>
                  {areas.map((area) => (
                    <option key={area.id} value={area.id}>
                      {area.name}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div className="admin-modal-field">
                <Form.Label>KDV Oranı (%)</Form.Label>
                <Form.Control type="number" step="1" min="0" max="100" value={kdvRate} onChange={(event) => setKdvRate(event.target.value)} className="form-input" />
              </div>
              <div className="admin-modal-field" style={{ gridColumn: '1 / -1' }}>
                <Form.Check
                  type="switch"
                  id="edit-item-is-available"
                  label={isAvailable ? 'Siparişe Açık' : 'Geçici olarak satışa kapalı'}
                  checked={isAvailable}
                  onChange={(event) => setIsAvailable(event.target.checked)}
                />
              </div>
            </div>
          </div>

          <ModifierGroupsManager
            value={modifierGroups}
            onChange={setModifierGroups}
            modalVisible={show}
            onPersistAttachedGroups={handlePersistModifierGroups}
          />
        </Form>
      </AdminFormModal>

      <UploadImageModal show={isUploadModalVisible} onClose={() => setIsUploadModalVisible(false)} onImageFileChange={setImageFile} onSubmit={handleImageUpload} />
    </>
  );
};

export default EditItemModal;
